// ── V158: 입고 기록 — 거래처 영수증을 보고 '실제로 받은 것'을 남긴다 ────────────────────
// 사장님: "발주서는 실제 그 물건이 구매되고 입고되어 비용으로 처리된 자료는 아니다.
//          단가표에 입력된 발주처마다 매일 어떤 식재료가 실제 입고되었는지 기록이 되어야 해."
//   · 발주서를 누르면 품목·수량이 채워지고, 받은 대로 고친다 (주문 수량을 옆에 보여줌)
//   · 단가가 단가표와 다르면 표시 → 저장할 때 단가표에도 반영 (기본 켜짐)
//   · 결제: 현금 · 위챗 · 알리페이 · 카드 · 계좌이체 · 외상(나중에 정산 → 외상 잔액)
//   · 손익의 식재료비 = 그 달 입고 합계
// 데이터: users/{매장}/receivings/{id} (영수증 한 장 = 문서 하나) · 사진은 receivingPhotos/{id}
//   { id, date, month, vendor, vendor_zh?, items: [{ key, okey, code?, name, name_zh?, spec?, unit, qty, price, amount, ordered? }],
//     missing?: [{ okey, name, unit, ordered }], total, receiptTotal?, payment, paid, paidAt?, paidMethod?,
//     orderIds?, hasPhoto?, memo?, createdAt, createdBy, updatedAt?, updatedBy? }
//   price = 1단위(근·개…) 값, qty = 받은 양(단위 기준) → amount = qty × price
// 발주서(orderHistory 항목)에는 receive: { state: 'full'|'partial'|'skip', ids: [], left?: { okey: 남은 양 }, at }
import { orderPackInfo, unitPriceOf, specPack } from './orderPack';
import { nameParts, splitKoZh } from './koZh';   // V161 한국어·중국어 이름 나누기
import { orderKeyOf } from './orderKey';
import { makeCodeAllocator } from './priceCodes';
import { todayKey } from './workCalc';

export const PAY_METHODS = ['cash', 'wechat', 'alipay', 'card', 'transfer', 'credit'];
export const PAY_LABEL = {
  cash:     { ko: '현금', zh: '现金', icon: '💵' },
  wechat:   { ko: '위챗', zh: '微信', icon: '💬' },
  alipay:   { ko: '알리페이', zh: '支付宝', icon: '🔷' },
  card:     { ko: '카드', zh: '刷卡', icon: '💳' },
  transfer: { ko: '계좌이체', zh: '转账', icon: '🏦' },
  credit:   { ko: '외상', zh: '赊账', icon: '📒' },
};
export const SETTLE_METHODS = ['cash', 'wechat', 'alipay', 'card', 'transfer'];

// ── V159: 계정과목 — "단가표·입고에 식재료 말고 소모품 등 다른 계정과목도. 숯 같은 것도 비용 처리를 해야 하니까"
//   입고 줄마다 계정과목. 단가표 품목은 그 품목의 계정과목이 따라온다 (row.acct, 없으면 구분으로: 소모품 → 소모품, 나머지 → 식재료)
//   손익: 식재료 → 식재료비, 나머지는 계정과목마다 따로 줄 (ProfitLoss)
export const ACCTS = ['food', 'drink', 'supply', 'fuel', 'repair', 'equip', 'etc'];
export const ACCT_LABEL = {
  food:   { ko: '식재료', zh: '食材', icon: '🥬', color: '#15803D', bg: '#F0FDF4' },
  drink:  { ko: '주류·음료', zh: '酒水饮料', icon: '🍶', color: '#1D4ED8', bg: '#EFF6FF' },
  supply: { ko: '소모품', zh: '消耗品', icon: '🧻', color: '#92400E', bg: '#FFFBEB' },
  fuel:   { ko: '연료(숯·가스)', zh: '燃料(炭·燃气)', icon: '🔥', color: '#C2410C', bg: '#FFF7ED' },
  repair: { ko: '수선·시설', zh: '维修·设施', icon: '🔧', color: '#475569', bg: '#F1F5F9' },
  equip:  { ko: '비품', zh: '器具设备', icon: '🍳', color: '#7C3AED', bg: '#F5F3FF' },
  etc:    { ko: '기타', zh: '其他', icon: '📦', color: '#52525B', bg: '#F4F4F5' },
};
export const isAcct = (a) => ACCTS.includes(a);
/** 단가표 줄의 계정과목 */
export function acctOfRow(p) {
  if (p && isAcct(p.acct)) return p.acct;
  return p && p.category === '소모품' ? 'supply' : 'food';
}
/** 입고 줄의 계정과목 (예전 기록은 식재료) */
export const acctOfLine = (l) => (l && isAcct(l.acct) ? l.acct : 'food');
/** 계정과목 → 단가표 구분 (새 줄을 넣을 때) */
export const categoryForAcct = (a) => (a === 'food' ? '신선식품' : a === 'drink' ? '공산품' : '소모품');
export const PENDING_DAYS = 7;          // 이보다 오래된 발주서는 입고 대기에 안 띄운다 (예전 발주서가 쌓이지 않게)
export const RECEIVINGS_LIMIT = 400;     // 불러올 최근 입고 기록 수
export const MEMO_MAX = 500;

const r2 = (n) => Math.round((Number(n) || 0) * 100) / 100;
const r3 = (n) => Math.round((Number(n) || 0) * 1000) / 1000;
const s = (x) => String(x == null ? '' : x).trim();
export const nameKey = (x) => s(x).replace(/\s+/g, '').toLowerCase();
const specKey = (x) => { const v = s(x); return v === '-' ? '' : v.replace(/\s+/g, '').toLowerCase(); };
/** 발주서 품목과 입고 줄을 잇는 열쇠 — 이름 + 규격 (발주서 저장 항목에는 코드가 없다) */
export const okeyOf = (name, spec) => `${nameKey(name)}|${specKey(spec)}`;

export const newReceivingId = () => 'rv' + Date.now().toString(36) + Math.random().toString(36).slice(2, 6);
export const newLineId = () => 'l' + Math.random().toString(36).slice(2, 9);

const addDays = (key, n) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(key || ''));
  if (!m) return '';
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]) + n);
  return todayKey(d);
};

/** Firestore는 undefined를 못 받는다 — 깊게 지운다 */
export function clean(v) {
  if (Array.isArray(v)) return v.map(clean).filter(x => x !== undefined);
  if (v && typeof v === 'object') {
    const o = {};
    for (const k of Object.keys(v)) { const c = clean(v[k]); if (c !== undefined) o[k] = c; }
    return o;
  }
  if (typeof v === 'number' && !isFinite(v)) return 0;
  return v;
}

// ── 단가표 ────────────────────────────────────────────────────────────────

/** 단가표 발주처 목록 — 품목 많은 순 { vendor, vendor_zh, count } */
export function vendorsOf(prices = []) {
  const m = new Map();
  for (const p of prices || []) {
    const v = s(p?.vendor);
    if (!v || (p?.category === '반제품')) continue;
    const cur = m.get(v) || { vendor: v, vendor_zh: '', count: 0 };
    cur.count += 1;
    const z = s(p.vendor_zh);
    if (z && !/[가-힣]/.test(z) && !cur.vendor_zh) cur.vendor_zh = z;
    m.set(v, cur);
  }
  return [...m.values()].sort((a, b) => b.count - a.count || a.vendor.localeCompare(b.vendor));
}

/** 발주서 품목 → 단가표 줄 (발주처·이름·규격이 같은 줄 → 발주처·이름 → 이름) */
export function findPriceRow(prices = [], { name, vendor, spec } = {}) {
  const n = nameKey(name);
  if (!n) return null;
  const list = (prices || []).filter(p => p && nameKey(p.name) === n && p.category !== '반제품');
  if (!list.length) return null;
  const v = s(vendor);
  const sp = specKey(spec);
  return list.find(p => s(p.vendor) === v && specKey(p.spec) === sp)
    || list.find(p => s(p.vendor) === v)
    || list[0];
}

/** 단가표 줄의 1단위 값 (중국 매장은 '2근 ¥10'이면 1근 ¥5) */
export function rowUnitPrice(p, packMode) {
  if (!p) return 0;
  return r3(unitPriceOf(Number(p.price) || 0, orderPackInfo(p, packMode)));
}

/** 단가표 줄 → 입고 줄 */
export function lineFromRow(p, packMode, qty = '') {
  return clean({
    id: newLineId(), key: orderKeyOf(p), okey: okeyOf(p.name, p.spec),
    code: s(p.code) || undefined, name: s(p.name), name_zh: s(p.name_zh) || undefined,
    spec: specKey(p.spec) ? s(p.spec) : undefined, unit: s(p.unit) || '개',
    qty, price: rowUnitPrice(p, packMode), ordered: 0, acct: acctOfRow(p),
  });
}

/** 단가표에 없는 새 품목 줄 */
export function newItemLine({ name, name_zh, unit, price, qty = '', acct = 'food', net_g }) {
  // V161: '청경채(上海青)'처럼 적어도 한국어·중국어 칸으로 나눈다
  const sp = name_zh ? { ko: s(name), zh: s(name_zh) } : splitKoZh(name);
  const ko = sp.ko || sp.zh;
  const g = Number(net_g);
  return clean({
    id: newLineId(), key: '', okey: okeyOf(ko, ''), name: ko, name_zh: sp.zh || undefined, unit: s(unit) || '개', qty, price: r3(price),
    ordered: 0, isNew: true, acct: isAcct(acct) ? acct : 'food', net_g: g > 0 ? g : undefined,
  });
}

// ── 발주서 → 입고 줄 ─────────────────────────────────────────────────────────

/** 입고 대기 발주서 — 최근 PENDING_DAYS일, 다 받지 않았고 '입고 안 함'이 아닌 것 (최근 순) */
export function pendingOrders(orderHistory = [], today = todayKey(), days = PENDING_DAYS) {
  const from = addDays(today, -days);
  return (orderHistory || [])
    .filter(h => h && Array.isArray(h.items) && h.items.length && s(h.date) >= from)
    .filter(h => !h.receive || h.receive.state === 'partial')
    .sort((a, b) => s(b.date).localeCompare(s(a.date)) || (Number(b.id) || 0) - (Number(a.id) || 0));
}

/** 발주서의 발주처 (V138부터 발주처별 저장. 예전 것은 품목이 모두 같은 발주처일 때만) */
export function orderVendor(h) {
  if (!h) return '';
  if (s(h.vendor)) return s(h.vendor);
  const vs = [...new Set((h.items || []).map(i => s(i.vendor)).filter(Boolean))];
  return vs.length === 1 ? vs[0] : '';
}

/** 남은(받을) 품목 — 일부 입고면 남은 양만 */
export function orderOpenItems(h) {
  const left = h?.receive?.state === 'partial' ? (h.receive.left || {}) : null;
  return (h?.items || [])
    .map(it => {
      const okey = okeyOf(it.name, it.spec);
      const qty = left ? Number(left[okey]) || 0 : Number(it.qty) || 0;
      return { ...it, okey, openQty: r3(qty) };
    })
    .filter(it => it.openQty > 0);
}

/**
 * 발주서(여러 장 가능, 같은 발주처) → 입고 줄. 같은 품목은 합친다
 * 단가: 단가표에 값이 있으면 지금 단가표 값, 없으면 발주서에 적힌 값
 */
export function linesFromOrders(orders = [], prices = [], packMode = false) {
  const byKey = new Map();
  for (const h of orders || []) {
    for (const it of orderOpenItems(h)) {
      const cur = byKey.get(it.okey);
      if (cur) { cur.ordered = r3(cur.ordered + it.openQty); cur.qty = cur.ordered; continue; }
      const row = findPriceRow(prices, { name: it.name, vendor: it.vendor || orderVendor(h), spec: it.spec });
      const tablePrice = row ? rowUnitPrice(row, packMode) : 0;
      byKey.set(it.okey, clean({
        id: newLineId(), key: row ? orderKeyOf(row) : '', okey: it.okey,
        code: row && s(row.code) ? s(row.code) : undefined,
        name: s(it.name), name_zh: s(row?.name_zh) || undefined,
        spec: specKey(it.spec) ? s(it.spec) : undefined, unit: s(it.unit) || s(row?.unit) || '개',
        ordered: it.openQty, qty: it.openQty,
        price: tablePrice > 0 ? tablePrice : r3(it.price),
        acct: row ? acctOfRow(row) : 'food',
      }));
    }
  }
  return [...byKey.values()];
}

// ── 계산 ──────────────────────────────────────────────────────────────────

export const lineQty = (l) => { const n = parseFloat(String(l?.qty ?? '').replace(',', '.')); return isFinite(n) && n > 0 ? n : 0; };
export const linePrice = (l) => { const n = parseFloat(String(l?.price ?? '').replace(',', '.')); return isFinite(n) && n >= 0 ? n : 0; };
export const lineAmount = (l) => r2(lineQty(l) * linePrice(l));
export const sumLines = (lines = []) => r2((lines || []).reduce((a, l) => a + lineAmount(l), 0));

/**
 * 단가표와 비교 — { row, base(단가표 1단위 값), ratio, state }
 *   state: 'same' · 'up' · 'down' · 'empty'(단가표 단가 없음) · 'new'(단가표에 없음)
 */
export function priceDiff(line, prices = [], packMode = false) {
  const row = line?.key ? (prices || []).find(p => p && orderKeyOf(p) === line.key) : null;
  if (!row) return { row: null, base: 0, ratio: 0, state: 'new' };
  const base = rowUnitPrice(row, packMode);
  const pr = linePrice(line);
  if (!(base > 0)) return { row, base: 0, ratio: 0, state: pr > 0 ? 'empty' : 'same' };
  const ratio = (pr - base) / base;
  if (Math.abs(pr - base) < 0.005 || Math.abs(ratio) < 0.005) return { row, base, ratio: 0, state: 'same' };
  return { row, base, ratio, state: ratio > 0 ? 'up' : 'down' };
}

/** 단가표에 반영할 후보 — 받은 줄 중 단가가 다르거나 비어 있는 것 · 새 품목 */
export function priceUpdateCandidates(lines = [], prices = [], packMode = false) {
  return (lines || [])
    .filter(l => lineQty(l) > 0 && linePrice(l) > 0)
    .map(l => ({ line: l, diff: priceDiff(l, prices, packMode) }))
    .filter(x => x.diff.state !== 'same');
}

const UNIT_G = { 근: 500, 斤: 500, kg: 1000, KG: 1000, 킬로: 1000, 公斤: 1000, g: 1 };

/**
 * 단가표 고치기 — chosen: 반영할 줄 id 집합
 * 있는 줄은 단가만 바꾼다 (묶음 값 줄이면 1단위 값 × 묶음). 새 품목은 새 줄로 넣는다
 * @returns { prices, changed, added, rowsByLine: { 입고 줄 id: 단가표 줄 } }
 */
export function applyPriceUpdates(prices = [], lines = [], chosen = new Set(), { packMode = false, vendor = '', vendor_zh = '' } = {}) {
  const next = [...(prices || [])];
  const alloc = makeCodeAllocator(next);
  let changed = 0; let added = 0;
  const rowsByLine = {};
  for (const { line, diff } of priceUpdateCandidates(lines, prices, packMode)) {
    if (!chosen.has(line.id)) continue;
    const unitP = linePrice(line);
    if (diff.row) {
      const k = next.findIndex(p => p === diff.row);
      if (k < 0) continue;
      const info = orderPackInfo(diff.row, packMode);
      const price = r2(info.pack > 1 && info.perPack ? unitP * info.pack : unitP);
      next[k] = { ...diff.row, price };
      rowsByLine[line.id] = next[k];
      changed += 1;
    } else {
      // V161: 단가표에 같은 이름(한국어나 중국어)이 이미 있으면 새 줄을 만들지 않고 그 줄에 잇는다 (중복 방지)
      const np = nameParts(line);
      const same = findSameNameRow(next, np, vendor);
      if (same) {
        const k = next.indexOf(same);
        if (!(Number(same.price) > 0) && k >= 0) { next[k] = { ...same, price: r2(unitP) }; changed += 1; }
        rowsByLine[line.id] = next[k >= 0 ? k : next.length - 1] || same;
        continue;
      }
      const g = UNIT_G[s(line.unit)] || 0;
      const a = acctOfLine(line);
      const ng = Number(line.net_g) > 0 ? Number(line.net_g) : g;
      const row = clean({
        code: alloc(), name: np.ko || s(line.name), name_zh: np.zh || s(line.name_zh) || '', category: categoryForAcct(a), acct: a === 'food' ? undefined : a,
        vendor: s(vendor), vendor_zh: s(vendor_zh) || undefined,
        spec: g ? `1${s(line.unit)}` : '', unit: s(line.unit) || '개',
        price: r2(unitP), net_g: ng || undefined,
      });
      next.push(row);
      rowsByLine[line.id] = row;
      added += 1;
    }
  }
  return { prices: next, changed, added, rowsByLine };
}

/** 단가표에서 이름이 같은 줄 (반제품 제외, 같은 발주처 먼저) — 한국어·중국어 어느 쪽이든 */
export function findSameNameRow(prices = [], { ko = '', zh = '' } = {}, vendor = '') {
  const kk = nameKey(ko); const zz = nameKey(zh);
  const hit = (p) => p && p.category !== '반제품' && ((kk && (nameKey(p.name) === kk || nameKey(p.name_zh) === kk)) || (zz && (nameKey(p.name_zh) === zz || nameKey(p.name) === zz)));
  const list = (prices || []).filter(hit);
  return list.find(p => s(p.vendor) === s(vendor)) || list[0] || null;
}

/** V161: 단가표 줄에 빠진 정보 — 단가 · 단위 · 중량(g)
 *  중량은 레시피 원가에 쓰는 식재료 · 주류·음료만 (숯·소모품·수선은 중량이 필요 없다) */
export function rowGaps(p) {
  if (!p || p.category === '반제품') return [];
  const gaps = [];
  if (!(Number(p.price) > 0)) gaps.push('price');
  if (!s(p.unit)) gaps.push('unit');
  const a = acctOfRow(p);
  if ((a === 'food' || a === 'drink') && !(Number(p.net_g) > 0)) gaps.push('net_g');
  return gaps;
}
/** 근·kg 품목은 중량을 계산할 수 있다 (규격 '10근' → 5000g). 못 하면 0 */
export function suggestNetG(p) {
  const u = s(p?.unit);
  const g = UNIT_G[u] || 0;
  if (!g) return 0;
  const pack = specPack(p?.spec, u) || 1;
  return Math.round(g * (pack > 0 ? pack : 1));
}
/** 빠진 정보 채우기 — fixes: { 발주키: { net_g?, unit?, price? } } */
export function applyRowFixes(prices = [], fixes = {}) {
  let n = 0;
  const next = (prices || []).map(p => {
    const f = fixes[orderKeyOf(p)];
    if (!f) return p;
    const patch = {};
    if (Number(f.net_g) > 0 && !(Number(p.net_g) > 0)) patch.net_g = Math.round(Number(f.net_g));
    if (s(f.unit) && !s(p.unit)) patch.unit = s(f.unit);
    if (Number(f.price) > 0 && !(Number(p.price) > 0)) patch.price = r2(f.price);
    if (!Object.keys(patch).length) return p;
    n += 1;
    return { ...p, ...patch };
  });
  return { prices: next, fixed: n };
}

/** 단가표에 새로 넣은 줄을 입고 줄에 이어 준다 (코드·발주 키) */
export function linkNewRows(lines = [], rowsByLine = {}) {
  return (lines || []).map(l => {
    const row = rowsByLine[l.id];
    if (!row) return l;
    const { isNew, ...rest } = l;
    return { ...rest, key: orderKeyOf(row), code: row.code || rest.code };
  });
}

// ── 저장할 문서 ───────────────────────────────────────────────────────────────

/** 입고 문서 만들기 — 받은 줄(수량 > 0)만 items, 주문했는데 안 온 줄은 missing */
export function buildReceivingDoc({
  id, date, vendor, vendor_zh, lines = [], receiptTotal, payment = 'cash', memo, orderIds = [], hasPhoto = false,
  who = '', prev = null, now = Date.now(),
}) {
  const items = (lines || []).filter(l => lineQty(l) > 0).map(l => { const np = nameParts(l); return clean({
    key: l.key || undefined, okey: l.okey || okeyOf(l.name, l.spec), code: l.code || undefined,
    name: np.ko || s(l.name), name_zh: np.zh || l.name_zh || undefined, spec: l.spec || undefined, unit: s(l.unit) || '개',
    qty: r3(lineQty(l)), price: r3(linePrice(l)), amount: lineAmount(l),
    ordered: Number(l.ordered) > 0 ? r3(l.ordered) : undefined, isNew: l.isNew || undefined,
    acct: acctOfLine(l),
  }); });
  const missing = (lines || []).filter(l => Number(l.ordered) > 0 && lineQty(l) <= 0)
    .map(l => clean({ okey: l.okey || okeyOf(l.name, l.spec), name: s(l.name), unit: s(l.unit) || '개', ordered: r3(l.ordered) }));
  const rt = parseFloat(String(receiptTotal ?? '').replace(',', '.'));
  const credit = payment === 'credit';
  const keepPaid = prev && prev.payment === 'credit' && credit;   // 외상 → 외상으로 고칠 때는 정산 상태 유지
  return clean({
    id: id || newReceivingId(),
    date, month: String(date || '').slice(0, 7),
    vendor: s(vendor), vendor_zh: s(vendor_zh) || undefined,
    items, missing: missing.length ? missing : undefined,
    total: sumLines(lines), receiptTotal: isFinite(rt) && rt > 0 ? r2(rt) : undefined,
    payment, paid: credit ? (keepPaid ? !!prev.paid : false) : true,
    paidAt: keepPaid && prev.paid ? prev.paidAt : undefined,
    paidMethod: keepPaid && prev.paid ? prev.paidMethod : undefined,
    orderIds: (orderIds || []).length ? orderIds : (prev?.orderIds || undefined),
    hasPhoto: hasPhoto ? true : undefined,
    memo: s(memo).slice(0, MEMO_MAX) || undefined,
    // V161: 메모 번역 — 메모가 그대로면 예전 번역을 그대로 둔다 (바뀌면 저장 뒤 다시 번역)
    memo_zh: prev && s(prev.memo) === s(memo).slice(0, MEMO_MAX) ? prev.memo_zh : undefined,
    memo_ko: prev && s(prev.memo) === s(memo).slice(0, MEMO_MAX) ? prev.memo_ko : undefined,
    createdAt: prev?.createdAt || now, createdBy: prev ? prev.createdBy : (s(who).slice(0, 30) || undefined),
    updatedAt: prev ? now : undefined, updatedBy: prev ? (s(who).slice(0, 30) || undefined) : undefined,
  });
}

/** 입고 문서 → 편집할 줄 (안 온 품목도 다시 보이게) */
export function linesFromReceiving(rec) {
  const items = (rec?.items || []).map(it => { const np = nameParts(it); return { ...it, name: np.ko || it.name, name_zh: np.zh || it.name_zh, id: newLineId(), qty: it.qty, price: it.price, ordered: Number(it.ordered) || 0 }; });
  const missing = (rec?.missing || []).map(m => ({ id: newLineId(), key: '', okey: m.okey, name: m.name, unit: m.unit, ordered: m.ordered, qty: 0, price: 0 }));
  return [...items, ...missing];
}

// ── 발주서 상태 ───────────────────────────────────────────────────────────────

/**
 * 입고한 뒤 발주서 상태 — 받은 양을 오래된 발주서부터 나눠 채운다
 * @returns 바뀐 발주서 항목들
 */
export function ordersAfterReceive(orders = [], lines = [], receivingId, now = Date.now()) {
  const got = new Map();
  for (const l of lines || []) {
    const q = lineQty(l);
    if (q > 0) got.set(l.okey || okeyOf(l.name, l.spec), (got.get(l.okey || okeyOf(l.name, l.spec)) || 0) + q);
  }
  const sorted = [...(orders || [])].sort((a, b) => s(a.date).localeCompare(s(b.date)) || (Number(a.id) || 0) - (Number(b.id) || 0));
  return sorted.map(h => {
    const left = {};
    for (const it of orderOpenItems(h)) {
      const have = got.get(it.okey) || 0;
      const take = Math.min(have, it.openQty);
      got.set(it.okey, r3(have - take));
      const rest = r3(it.openQty - take);
      if (rest > 0.0005) left[it.okey] = rest;
    }
    const ids = [...new Set([...(h.receive?.ids || []), receivingId])];
    const receive = Object.keys(left).length ? { state: 'partial', ids, left, at: now } : { state: 'full', ids, at: now };
    return { ...h, receive };
  });
}

/** 입고 기록을 지웠을 때 — 연결된 발주서를 다시 입고 대기로 (다른 입고가 남아 있으면 그대로) */
export function ordersAfterDelete(orderHistory = [], rec) {
  const ids = new Set(rec?.orderIds || []);
  return (orderHistory || []).filter(h => h && ids.has(h.id) && h.receive).map(h => {
    const rest = (h.receive.ids || []).filter(x => x !== rec.id);
    if (!rest.length) { const { receive, ...o } = h; return o; }
    return { ...h, receive: { ...h.receive, ids: rest } };
  });
}

/** 발주서를 '입고 안 함'으로 */
export const orderSkipped = (h, now = Date.now()) => ({ ...h, receive: { state: 'skip', ids: h.receive?.ids || [], at: now } });

// ── 외상 · 합계 ───────────────────────────────────────────────────────────────

/** 외상 잔액 — 발주처별 { vendor, vendor_zh, unpaid, count, oldest, items: [입고] } (많은 순) */
export function creditBalances(receivings = []) {
  const m = new Map();
  for (const r of receivings || []) {
    if (!r || r.payment !== 'credit' || r.paid) continue;
    const v = s(r.vendor) || '-';
    const cur = m.get(v) || { vendor: v, vendor_zh: s(r.vendor_zh), unpaid: 0, count: 0, oldest: r.date, list: [] };
    cur.unpaid = r2(cur.unpaid + (Number(r.total) || 0));
    cur.count += 1;
    if (s(r.date) < s(cur.oldest)) cur.oldest = r.date;
    cur.list.push(r);
    m.set(v, cur);
  }
  for (const c of m.values()) c.list.sort((a, b) => s(a.date).localeCompare(s(b.date)));
  return [...m.values()].sort((a, b) => b.unpaid - a.unpaid);
}

export const settlePatch = ({ date, method }) => ({ paid: true, paidAt: date, paidMethod: method });
export const unsettlePatch = () => ({ paid: false, paidAt: null, paidMethod: null });

/** 입고 한 장의 계정과목별 금액 { food: n, … } — 줄 금액으로 나누고, 줄이 없으면 전부 식재료 */
export function acctAmounts(r) {
  const out = {};
  const items = (r && r.items) || [];
  if (!items.length) { if (Number(r?.total) > 0) out.food = r2(r.total); return out; }
  for (const it of items) {
    const a = acctOfLine(it);
    out[a] = r2((out[a] || 0) + (Number(it.amount) || 0));
  }
  return out;
}

/** 그 달 입고 합계 · 발주처별 · 계정과목별 (byAcct: { food, drink, … }) */
export function monthSummary(receivings = [], month = '') {
  const list = (receivings || []).filter(r => r && s(r.date).startsWith(month));
  const byVendor = new Map();
  const byAcct = {};
  for (const r of list) {
    for (const [a, v] of Object.entries(acctAmounts(r))) byAcct[a] = r2((byAcct[a] || 0) + v);
    const v = s(r.vendor) || '-';
    const cur = byVendor.get(v) || { vendor: v, vendor_zh: s(r.vendor_zh), total: 0, count: 0, unpaid: 0 };
    cur.total = r2(cur.total + (Number(r.total) || 0));
    cur.count += 1;
    if (r.payment === 'credit' && !r.paid) cur.unpaid = r2(cur.unpaid + (Number(r.total) || 0));
    byVendor.set(v, cur);
  }
  return {
    total: r2(list.reduce((a, r) => a + (Number(r.total) || 0), 0)),
    count: list.length,
    byVendor: [...byVendor.values()].sort((a, b) => b.total - a.total),
    byAcct,
  };
}

export const receivingMonthTotal = (receivings, month) => monthSummary(receivings, month).total;

/** 발주처의 최근 결제 방법 (입고할 때 기본값) */
export function lastPaymentOf(receivings = [], vendor = '') {
  const r = (receivings || []).filter(x => x && s(x.vendor) === s(vendor))
    .sort((a, b) => (Number(b.createdAt) || 0) - (Number(a.createdAt) || 0))[0];
  return r && PAY_METHODS.includes(r.payment) ? r.payment : '';
}
